import { Autocomplete, debounce, ListItem, ListItemText } from "@mui/material";
import { SxProps } from "@mui/system";
import { useEffect, useMemo } from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import AutocompleteGroupedList from "../../AutocompleteGroupedList/AutocompleteGroupedList";
import CommonTextField from "../../commonTextField/CommonTextField";
import useWorkLocationAutocomplete, {
  WorkLocationOption,
} from "./useWorkLocationAutocomplete";

type Props = {
  name: string;
  fullWidth?: boolean;
  isLoading?: boolean;
  inputSx?: SxProps;
  customerCompanyId?: string;
  customerOrgId?: string;
  groupByCustomerOrg?: boolean;
  includeInactive?: boolean;
  disabled?: boolean;
  required?: boolean;
  useFixedOptions?: boolean;
  rules?: RegisterOptions;
  /**
   * Optional override for onChange.
   * Use this to perform side effects like resetting dependent fields
   * when workLocation changes.
   */
  onChangeOverride?: (val: WorkLocationOption | null) => void;
  error?: boolean;
  placeholder?: string;
};

const WorkLocationAutocompleteField = ({
  name,
  fullWidth,
  isLoading = false,
  inputSx = {},
  customerCompanyId,
  customerOrgId,
  groupByCustomerOrg = true,
  includeInactive = false,
  disabled,
  required = false,
  useFixedOptions = true,
  rules,
  onChangeOverride,
  error = false,
  placeholder,
}: Props) => {
  const { workLocationOptions, setSearchTerm } = useWorkLocationAutocomplete({
    customerCompanyId,
    customerOrgId,
    useFixedOptions,
  });

  const { control } = useFormContext();

  // Debounced backend search (400ms delay to avoid excessive API calls)
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string, reason: string) => {
        if (reason === "input") {
          setSearchTerm(value);
        }
      }, 400),
    [setSearchTerm],
  );

  // Cancel debounce when customerOrgId changes (to prevent stale debounced calls)
  useEffect(() => {
    debouncedSearch.clear();
  }, [customerOrgId, debouncedSearch]);

  // Filter out inactive work locations if needed
  const filteredOptions = includeInactive
    ? workLocationOptions
    : workLocationOptions.filter((wl) => wl.active);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        ...(required && {
          required: { value: true, message: `${name}_required` },
        }),
        ...rules,
      }}
      render={({ field: { value, onChange } }) => (
        <Autocomplete<WorkLocationOption, false, undefined, undefined>
          id="work-location-autocomplete"
          disabled={disabled}
          fullWidth={fullWidth}
          multiple={false}
          sx={{
            // workaround to prevent component to resize on hover/focus -> updating the MUI package should fix this
            "& :hover .MuiAutocomplete-input, & .Mui-focused .MuiAutocomplete-input":
              { minWidth: "30px" },
          }}
          value={value || null}
          loading={isLoading}
          options={filteredOptions}
          getOptionLabel={(option) => option.name}
          {...(groupByCustomerOrg && {
            groupBy: (option) => option.customerOrgName || "",
          })}
          renderOption={(props, option) => (
            <ListItem {...props} key={option.id}>
              <ListItemText>{option.name}</ListItemText>
            </ListItem>
          )}
          {...(groupByCustomerOrg && {
            renderGroup: ({ group, children }) => (
              <AutocompleteGroupedList
                key={group}
                group={group}
                children={children}
              />
            ),
          })}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, newValue) => {
            if (onChangeOverride) onChangeOverride(newValue);
            else onChange(newValue);
          }}
          onInputChange={(_, value, reason) => debouncedSearch(value, reason)}
          renderInput={(params) => (
            <CommonTextField
              {...params}
              sx={inputSx}
              error={error}
              placeholder={placeholder}
            />
          )}
        />
      )}
    />
  );
};

export default WorkLocationAutocompleteField;
